/** The change-history rows a change set wrote — its correlation id across the generic record
 *  history, the notification history and the tenant-config history — oldest first. The same
 *  rows carry the change-set chip on every History tab; this is the view from the other side. */
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { Link as RouterLink } from 'react-router-dom';

import * as aiChangesApi from '@/api/aiChanges';
import { aiChangeKeys, type AIChangeHistoryRow } from '@/api/aiChanges';
import { ConsoleApiError } from '@/api/client';
import { act, ValueActions } from '@/components/ValueActions/ValueActions';
import { formatDate, formatRelativeTime } from '@/utils/format';
import { consoleRouteFor } from '@/utils/gridLinks';

import { actorActions } from './aiChangeLinks';
import { modelName } from './AIChangeEntries';

const HISTORY_KIND: Record<AIChangeHistoryRow['history'], string> = {
  objchange: 'Record',
  nfchange: 'Notification',
  topconf: 'Tenant config',
};

const MAX_PATH_CHIPS = 5;

/** Where a history row is read in full: a tenant-config change has its own dialog URL, the
 *  others open the record they changed. */
function historyRowHref(row: AIChangeHistoryRow): string | null {
  if (row.history === 'topconf') {
    return `/tour-operators/${encodeURIComponent(row.object_pk)}/configuration/history/${row.id}`;
  }
  return consoleRouteFor(row.content_type, row.object_pk);
}

export function AIChangeLinkedHistory({ changeId }: { changeId: string }) {
  const query = useQuery({
    queryKey: aiChangeKeys.history(changeId),
    queryFn: () => aiChangesApi.history(changeId),
  });

  if (query.isPending) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }
  if (query.isError) {
    return (
      <Alert severity="error">
        Couldn’t load the linked history.
        {query.error instanceof ConsoleApiError ? ` ${query.error.message}` : ''}
      </Alert>
    );
  }
  const { results, truncated } = query.data;
  if (results.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        This change set wrote no change-history rows.
      </Typography>
    );
  }

  return (
    <Box>
      <TableContainer data-ai-change-history>
        <Table size="small" aria-label="Change history this change set wrote">
          <TableHead>
            <TableRow>
              <TableCell>History</TableCell>
              <TableCell>When</TableCell>
              <TableCell>Record</TableCell>
              <TableCell>Action</TableCell>
              <TableCell>By</TableCell>
              <TableCell>Via</TableCell>
              <TableCell>Changed</TableCell>
              <TableCell>Comment</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {results.map((row) => {
              const to = historyRowHref(row);
              const text = row.object_repr || `${modelName(row.content_type)} #${row.object_pk}`;
              return (
                <TableRow key={`${row.history}:${row.id}`} data-history-row={row.history}>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    <Chip size="small" variant="outlined" label={HISTORY_KIND[row.history]} />
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    <Tooltip title={formatDate(row.created_ts)} describeChild>
                      <span>{formatRelativeTime(row.created_ts)}</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell sx={{ minWidth: 160 }}>
                    <ValueActions
                      actions={[
                        ...(to ? [act.open(to, 'Open record')] : []),
                        act.copy(row.object_pk, 'Copy record pk'),
                      ]}
                    >
                      {to ? (
                        <Link component={RouterLink} to={to} underline="hover">
                          {text}
                        </Link>
                      ) : (
                        text
                      )}
                    </ValueActions>
                  </TableCell>
                  <TableCell>{row.action}</TableCell>
                  <TableCell>
                    {row.actor ? (
                      <ValueActions actions={actorActions(row.actor)}>
                        {row.actor.email}
                      </ValueActions>
                    ) : (
                      <em>system</em>
                    )}
                  </TableCell>
                  <TableCell>{row.source || '—'}</TableCell>
                  <TableCell sx={{ minWidth: 160 }}>
                    {row.changed_paths.slice(0, MAX_PATH_CHIPS).map((p) => (
                      <Chip
                        key={p}
                        size="small"
                        variant="outlined"
                        label={p}
                        sx={{ mr: 0.5, mb: 0.25 }}
                      />
                    ))}
                    {row.changed_paths.length > MAX_PATH_CHIPS && (
                      <Chip size="small" label={`+${row.changed_paths.length - MAX_PATH_CHIPS}`} />
                    )}
                  </TableCell>
                  <TableCell sx={{ minWidth: 160 }}>{row.comment || '—'}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      {truncated && (
        <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 1 }}>
          Showing the first {results.length} rows — the change set wrote more.
        </Typography>
      )}
    </Box>
  );
}
